import { asQuantityUnit } from '../../utils/commercialQuantity';
import type { QuantityUnit } from '../../utils/commercialQuantity';
import type { OrderProductPayload } from './api/orderProductsApi';
import { omitUnusedOrderProductHeight } from './orderProductDimensions';

/**
 * One customer-order line as an editor holds it (the „Sprzedaż” editor of
 * `OrderProductsSection` and the Szkic step of the ZK card). `id` of a line
 * that is not saved yet is absent or starts with `new-`.
 */
export interface OrderProductPayloadSource {
  id?: string;
  productVariantId?: string | null;
  name?: string;
  quantity?: number | string | null;
  unit?: string | null;
  saleUnit?: string | null;
  saleQuantity?: number | string | null;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  conversionQuantity?: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unitPrice?: number;
  currency?: string;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  productCategoryDimensionDisplayProfile?: string | null;
  description?: string;
  cnCode?: string;
  netWeight?: number | null;
  grossWeight?: number | null;
  countryOfOrigin?: string;
}

/**
 * The single mapping of an edited line to the `PUT /orders/:id/products`
 * (full replacement) and `POST /orders` line payload. Without the sale-price
 * capability the price and currency are left out (the backend keeps them).
 */
export function toOrderProductPayload(
  product: OrderProductPayloadSource,
  options: { includeSalePrices: boolean; fallbackCurrency: string },
): OrderProductPayload {
  const quantity = Number(product.quantity);
  const base = {
    id: !product.id || product.id.startsWith('new-') ? undefined : product.id,
    productVariantId: product.productVariantId ?? null,
    name: String(product.name || '').trim(),
    quantity,
    unit: asQuantityUnit(product.unit),
    saleUnit: asQuantityUnit(product.saleUnit || product.unit) as QuantityUnit,
    saleQuantity: Number(product.saleQuantity ?? product.quantity),
    conversionUnit: product.conversionUnit ?? null,
    conversionQuantity: product.conversionQuantity,
    width: product.width,
    height: product.height,
    length: product.length,
    thickness: product.thickness,
    productCategoryDimensionDisplayProfile: product.productCategoryDimensionDisplayProfile,
    description: product.description,
    cnCode: product.cnCode,
    netWeight: product.netWeight,
    grossWeight: product.grossWeight,
    countryOfOrigin: product.countryOfOrigin,
  };
  const withPrices = options.includeSalePrices
    ? { ...base, unitPrice: product.unitPrice ?? 0, currency: product.currency || options.fallbackCurrency }
    : base;
  // The display profile only decides whether height is used; it is not persisted.
  const { productCategoryDimensionDisplayProfile: _profile, ...payload } = omitUnusedOrderProductHeight(withPrices);
  return payload as OrderProductPayload;
}
